import { useEffect, useState } from "react";
import { useLocale } from "next-intl";
import { useRouter } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { useGetMyCartById } from "@lib/commerce/hooks/cartOperations";
import { getPagePathByLocale, orderConfirmationPage } from "@lib/constants/pagePaths";
import { QueryKeys } from "@lib/enums/QueryKeys";
import { cartIsEmpty } from "@lib/utils/cart-utility/cartIsEmpty";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import {
    clearPendingPlacedOrder,
    readCompletedPaymentCartId,
    readPendingPlacedOrder,
} from "../utils/adyen/pendingPaymentStorage";
import type { Cart } from "@graphql/generated/types";

type PlacedOrderCart = {
    cart: Cart | undefined;
    /** Loading the placed order's cart after a reload, or leaving for its confirmation. */
    isResolvingPlacedOrder: boolean;
    /** The cart shown belongs to an unpaid UPI order, so checkout belongs on payment. */
    isPlacedOrderPending: boolean;
};

/**
 * HEAP places a UPI order before its QR is paid, so the store loses the cart mid-payment. Keeps
 * that cart so the QR stays mounted. After a reload it loads the cart by id, so the payment step
 * mounts and restores the stored QR.
 */
export const usePlacedOrderCart = (
    storeCart: Cart | null | undefined,
    isCartResolved: boolean
): PlacedOrderCart => {
    const locale = useLocale();
    const { replace } = useRouter();
    const getMyCartById = useGetMyCartById();
    const [lastCart, setLastCart] = useState<Cart | undefined>();
    if (storeCart && !cartIsEmpty(storeCart) && storeCart !== lastCart) {
        setLastCart(storeCart);
    }

    const isStoreCartEmpty = cartIsEmpty(storeCart);
    const pendingCartId = isStoreCartEmpty ? readPendingPlacedOrder()?.cartId : undefined;
    // Success clears the pending entry before navigating, so the completed cart is held too.
    const isLastCartConsumed =
        !!lastCart &&
        (pendingCartId === lastCart.id || readCompletedPaymentCartId() === lastCart.id);

    const shouldRecoverCart =
        isCartResolved && isStoreCartEmpty && !!pendingCartId && !isLastCartConsumed;
    const recovery = useQuery({
        queryKey: [QueryKeys.getPlacedOrderCart, pendingCartId, locale],
        queryFn: async () => (await getMyCartById(pendingCartId ?? "")) ?? null,
        enabled: shouldRecoverCart,
        retry: false,
        staleTime: Infinity,
    });
    const recoveredCart =
        shouldRecoverCart && recovery.data && !cartIsEmpty(recovery.data)
            ? recovery.data
            : undefined;
    // Held from here on like a live one, so success still holds it until navigation.
    if (recoveredCart && recoveredCart !== lastCart) {
        setLastCart(recoveredCart);
    }

    const cart = isStoreCartEmpty && isLastCartConsumed ? lastCart : (storeCart ?? undefined);

    // Only when that cart cannot be loaded: the QR is then unrecoverable here.
    const isOpeningPlacedOrder =
        shouldRecoverCart && (recovery.isError || (recovery.isSuccess && !recoveredCart));

    useEffect(() => {
        if (!isOpeningPlacedOrder) return;
        const pending = readPendingPlacedOrder();
        if (!pending) return;
        const { orderNumber, orderDraftId } = pending.placedOrder;
        // One-shot, so going back does not redirect again.
        clearPendingPlacedOrder();
        Logger.info(ServiceType.ADYEN, "Opening placed order whose cart could not be loaded", {
            tag: LogTag.PAYMENT,
            orderNumber,
        });
        replace(
            `${getPagePathByLocale(orderConfirmationPage, locale)}/v2/${orderDraftId || orderNumber}`
        );
    }, [isOpeningPlacedOrder, locale, replace]);

    return {
        cart,
        isResolvingPlacedOrder: (shouldRecoverCart && recovery.isPending) || isOpeningPlacedOrder,
        isPlacedOrderPending: !!pendingCartId && cart?.id === pendingCartId,
    };
};
